'use client';

import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { SCAPIShopperProductsSchemas } from 'src/types';
import { useProductImages } from '@/hooks/product/use-product-images';
import { DynamicImage } from '@/components/dynamic-image';
import type { DynamicImageDimensions } from '@/lib/images/dynamic-image';
import { Carousel, CarouselContent, CarouselDots, CarouselItem } from '@/components/ui/carousel';
import InlineTruncationText from '@/components/inline-truncation-text';
import { focusVisibleOutline } from '@/lib/focus-styles';
import { cn } from '@/lib/utils';
import { resolveImageAlt } from '@/lib/images/resolve-image-alt';
import { PRODUCT_IMAGE_VIEW_TYPES } from '@/lib/product-image-view-types';

interface EventCourseDescriptionTabProps {
    product: SCAPIShopperProductsSchemas['Product'];
}

/** Gallery images: up to two 5-col tiles per row on desktop (~42vw each). */
const GALLERY_IMAGE_WIDTHS: DynamicImageDimensions = { base: '100vw', lg: '42vw', '2xl': 600 };

const MOBILE_CAROUSEL_IMAGE_WIDTHS: DynamicImageDimensions = { base: '85vw' };

const longDescriptionProseClassName = 'max-w-none text-neutral-40 marker:text-black [&_ul]:list-disc [&_ul]:pl-5';

const galleryImageFrameClassName = 'w-full overflow-hidden rounded-lg';

const galleryImageCellClassName = 'flex h-full w-full items-center';

const LONG_DESCRIPTION_COLLAPSED_CHAR_LIMIT = 250;

const readMoreButtonClassName = cn(
    'label-l w-fit cursor-pointer text-left uppercase text-neutral-10',
    focusVisibleOutline
);

/**
 * "Descrizione" tab content for event/course PDPs.
 *
 * Mobile — short description with a collapsible long body, then a peek carousel for
 * gallery images. Desktop — two-column intro with the same collapsible long body
 * and a gallery grid (one image: 3 cols from the content start; two or more:
 * 5-col pairs across the central 10 cols), preserving each image's natural aspect ratio.
 * When the product has no gallery images, the long description is shown in full.
 */
export default function EventCourseDescriptionTab({ product }: EventCourseDescriptionTabProps): ReactElement {
    const { t } = useTranslation('product');
    const { galleryImages } = useProductImages({ product, viewType: PRODUCT_IMAGE_VIEW_TYPES.SFA });
    const shortDescription = product.shortDescription;
    const longDescription = product.longDescription;

    const galleryAriaLabel = t('eventCourse.imageCarouselAriaLabel', { productName: product.name ?? '' });

    return (
        <div className="flex flex-col gap-6 lg:gap-10 pb-12">
            {(shortDescription || longDescription) && (
                <div className="flex flex-col gap-4 lg:grid lg:grid-cols-2 lg:gap-12">
                    {shortDescription && (
                        <h2
                            className="heading-title-l text-neutral-10 [&_p]:mb-2"
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: shortDescription }}
                        />
                    )}
                    {longDescription != null &&
                        longDescription.length > 0 &&
                        (galleryImages.length === 0 ? (
                            <div
                                className={longDescriptionProseClassName}
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{ __html: longDescription }}
                            />
                        ) : (
                            <InlineTruncationText
                                content={longDescription}
                                contentType="html"
                                truncation={{ mode: 'chars', value: LONG_DESCRIPTION_COLLAPSED_CHAR_LIMIT }}
                                readMoreLabel={t('eventCourse.readMore')}
                                readLessLabel={t('eventCourse.readLess')}
                                wrapperClassName="flex flex-col gap-4"
                                textClassName={longDescriptionProseClassName}
                                readMoreClassName={readMoreButtonClassName}
                                readLessClassName={readMoreButtonClassName}
                            />
                        ))}
                </div>
            )}

            {galleryImages.length > 0 && (
                <Carousel
                    className="-mr-4 w-auto lg:hidden pb-8"
                    opts={{ align: 'start', containScroll: false }}
                    aria-label={galleryAriaLabel}>
                    <CarouselContent className="ml-0 flex-nowrap items-center gap-3">
                        {galleryImages.map((image) => (
                            <CarouselItem
                                key={image.src}
                                className="flex basis-[min(100%,calc(100vw-4.5rem))] shrink-0 items-center pl-0">
                                <div className={galleryImageFrameClassName}>
                                    <DynamicImage
                                        src={image.src}
                                        alt={resolveImageAlt({ alt: image.alt, name: product.name })}
                                        className="h-auto w-full"
                                        loading="lazy"
                                        widths={MOBILE_CAROUSEL_IMAGE_WIDTHS}
                                    />
                                </div>
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                    {galleryImages.length > 1 && (
                        <CarouselDots
                            className="justify-start py-4"
                            activeDotClassName="bg-neutral-10"
                            inactiveDotClassName="border border-neutral-10 bg-transparent"
                            getDotAriaLabel={(index) => t('eventCourse.imageCarouselDotLabel', { index: index + 1 })}
                        />
                    )}
                </Carousel>
            )}

            {galleryImages.length > 0 && (
                <div className="hidden lg:grid lg:grid-cols-10 lg:gap-x-1 lg:gap-y-6 pb-20">
                    {galleryImages.map((image, index) => (
                        <div
                            key={image.src}
                            className={cn(
                                galleryImageCellClassName,
                                galleryImages.length === 1
                                    ? 'col-span-3 col-start-1'
                                    : cn('col-span-5', index % 2 === 0 ? 'col-start-1' : 'col-start-6')
                            )}>
                            <div className={galleryImageFrameClassName}>
                                <DynamicImage
                                    src={image.src}
                                    alt={resolveImageAlt({ alt: image.alt, name: product.name })}
                                    className="h-auto w-full"
                                    loading="lazy"
                                    widths={GALLERY_IMAGE_WIDTHS}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
